import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, CheckCircle2, Loader2, Inbox, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { TIME_SLOTS, slotLabel, todayStr, formatDateIt } from "@/lib/timeSlots";

const emptyForm = { date: "", slot: "", class_name: "", teacher_id: "" };

export default function SubstitutionsPanel() {
  const [date, setDate] = useState(todayStr());
  const [subs, setSubs] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);
  const [notifyingAll, setNotifyingAll] = useState(false);
  const [pendingNotify, setPendingNotify] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/substitutions?date=${date}`),
        api.get(`/teachers`),
      ]);
      setSubs(s.data);
      setTeachers(t.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, date }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ date: s.date, slot: String(s.slot), class_name: s.class_name, teacher_id: s.teacher_id });
    setOpen(true);
  };

  const save = async () => {
    if (!form.date || !form.slot || !form.class_name.trim() || !form.teacher_id) {
      toast.error("Compila data, ora, classe e docente");
      return;
    }
    setSaving(true);
    try {
      const payload = { date: form.date, slot: Number(form.slot), class_name: form.class_name, teacher_id: form.teacher_id };
      if (editing) {
        await api.put(`/substitutions/${editing.id}`, payload);
        toast.success("Sostituzione aggiornata");
        setOpen(false);
        setDate(form.date);
        load();
      } else {
        const { data } = await api.post(`/substitutions`, payload);
        toast.success(`Sostituzione aggiunta per ${formatDateIt(form.date)}`);
        setOpen(false);
        setDate(form.date);
        load();
        setPendingNotify(data);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/substitutions/${id}`);
      toast.success("Sostituzione eliminata");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const notify = async (id) => {
    setNotifyingId(id);
    try {
      await api.post(`/substitutions/${id}/notify`);
      toast.success("Email inviata al docente");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setNotifyingId(null);
    }
  };

  const notifyAll = async () => {
    setNotifyingAll(true);
    try {
      const { data } = await api.post(`/substitutions/notify-all?date=${date}`);
      const extra = data.failed ? ` (${data.failed} non inviati)` : "";
      toast.success(`Avvisi inviati: ${data.sent}/${data.total}${extra}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setNotifyingAll(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sostituzioni</h1>
          <p className="text-muted-foreground mt-1">Gestisci le sostituzioni giornaliere dei docenti.</p>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="date-filter" className="text-xs">Giorno da visualizzare</Label>
            <Input
              id="date-filter"
              type="date"
              data-testid="date-filter-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-[170px]"
            />
          </div>
          <Button
            variant="outline"
            data-testid="notify-all-btn"
            onClick={notifyAll}
            disabled={notifyingAll || subs.length === 0}
            className="h-10 gap-2"
          >
            {notifyingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}
            Avvisa tutti
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-substitution-btn" onClick={openNew} className="gap-2 h-10">
                <Plus className="h-4 w-4" /> Nuova
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="substitution-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editing ? "Modifica sostituzione" : "Nuova sostituzione"}
                </DialogTitle>
                <DialogDescription>Indica ora, classe e docente della sostituzione.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="sub-date">Data</Label>
                  <Input
                    id="sub-date"
                    type="date"
                    data-testid="sub-date-input"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ora</Label>
                  <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                    <SelectTrigger data-testid="slot-select"><SelectValue placeholder="Seleziona l'ora" /></SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((s) => (
                        <SelectItem key={s.n} value={String(s.n)}>{s.n}ª ora — {s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="class-input">Classe</Label>
                  <Input
                    id="class-input"
                    data-testid="class-input"
                    placeholder="es. 3A"
                    value={form.class_name}
                    onChange={(e) => setForm({ ...form, class_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Docente</Label>
                  <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                    <SelectTrigger data-testid="teacher-select"><SelectValue placeholder="Seleziona il docente" /></SelectTrigger>
                    <SelectContent>
                      {teachers.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">Nessun docente. Aggiungilo nella sezione Docenti.</div>}
                      {teachers.filter((t) => (t.status || "approved") !== "pending").map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name} — {t.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="save-substitution-btn" onClick={save} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-md border border-border bg-background overflow-hidden">
        <table className="w-full text-sm" data-testid="substitutions-table">
          <thead>
            <tr className="border-b border-border bg-secondary/60 text-left">
              <th className="px-4 py-3 font-semibold w-40">Ora</th>
              <th className="px-4 py-3 font-semibold w-24">Classe</th>
              <th className="px-4 py-3 font-semibold">Docente</th>
              <th className="px-4 py-3 font-semibold w-40">Notifica</th>
              <th className="px-4 py-3 font-semibold w-28">Conferma</th>
              <th className="px-4 py-3 font-semibold w-44 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Caricamento…</td></tr>
            ) : subs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  <Inbox className="mx-auto h-8 w-8 mb-2 opacity-50" />
                  Nessuna sostituzione per {formatDateIt(date)}.
                </td>
              </tr>
            ) : (
              subs.map((s) => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-3 font-mono">{slotLabel(s.slot)}</td>
                  <td className="px-4 py-3"><span className="inline-flex rounded-md bg-primary/10 text-primary px-2 py-0.5 font-semibold">{s.class_name}</span></td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.teacher_name}</div>
                    <div className="text-xs text-muted-foreground">{s.teacher_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {s.notified ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 className="h-4 w-4" /> Inviata</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Da inviare</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(s.notified_channels || []).includes("telegram") ? (
                      s.confirmed ? (
                        <span data-testid={`confirm-status-${s.id}`} title="Confermata dal docente su Telegram" className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600">
                          <span className="h-2.5 w-2.5 rounded-full bg-green-500 shrink-0" /> Confermata
                        </span>
                      ) : (
                        <span data-testid={`confirm-status-${s.id}`} title="In attesa di conferma su Telegram" className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600">
                          <span className="h-2.5 w-2.5 rounded-full bg-red-500 shrink-0" /> In attesa
                        </span>
                      )
                    ) : (
                      <span data-testid={`confirm-status-${s.id}`} title="Conferma non prevista (nessun invio Telegram)" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40 shrink-0" /> —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm" variant="ghost" data-testid={`notify-btn-${s.id}`}
                        onClick={() => notify(s.id)} disabled={notifyingId === s.id}
                        title="Invia email" className="text-primary"
                      >
                        {notifyingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`edit-btn-${s.id}`} onClick={() => openEdit(s)} title="Modifica">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-btn-${s.id}`} title="Elimina" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare la sostituzione?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {slotLabel(s.slot)} — classe {s.class_name} — {s.teacher_name}. Azione irreversibile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${s.id}`} onClick={() => remove(s.id)}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!pendingNotify} onOpenChange={(o) => { if (!o) setPendingNotify(null); }}>
        <AlertDialogContent data-testid="notify-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Inviare la comunicazione?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingNotify &&
                `Vuoi avvisare subito ${pendingNotify.teacher_name} della sostituzione (${slotLabel(pendingNotify.slot)}, classe ${pendingNotify.class_name})?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skip-notify-btn">No, più tardi</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-notify-btn"
              onClick={() => { const id = pendingNotify.id; setPendingNotify(null); notify(id); }}
            >
              Sì, invia ora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
